/**
 * SiteArea — 공사현황 우측 현장 패널 (플랫폼 apps/shell-web/SiteArea.tsx 구조 복제)
 *
 * 토글: 패널 왼쪽 끝 원형 버튼 (닫힘 w-0 / 열림 w-[308px]).
 * 헤더: 현장 목록 + 새 현장 버튼 → SiteRegistrationPopup(공용) 재사용.
 * 내부: SiteList (공사현황 현장 목록).
 */
import { useState } from 'react';
import { Button } from '@indipia/ui-core';
import { SiteRegistrationPopup } from '@indipia/shared-features';
import { useSitePanel } from './SitePanelContext';
import { SiteList } from './SiteList';
import type { WorklogListItem } from '../../types/worklog.types';

const WORK_LOG_AGENT_ID = 2; // 인디공사현황 numericAgentId

export function SiteArea({
  onFirstLoad,
  isSiteSelected,
  onToggleSite,
  onSiteRegistered,
  onSitesLoaded,
  onSiteDeleted,
  showRegister = true,
}: {
  onFirstLoad?: () => void;
  isSiteSelected?: (siteId: number) => boolean;
  /** 2번째 인자 = 클릭된 현장 항목(목록 원본). basic-info에 없는 현장 선택 시 화면이 이걸로 상세를 그린다. */
  onToggleSite?: (siteId: number, item: WorklogListItem) => void;
  /** 현장 등록/확인 완료 시 그 siteId 통보 — 페이지가 현장패널 선택(나머지 해제)에 사용 */
  onSiteRegistered?: (siteId: number) => void;
  /** 현장 목록 조회 완료 시 전체 siteId 통보 — 단일 선택 계산용 */
  onSitesLoaded?: (siteIds: number[], items: WorklogListItem[]) => void;
  /** 현장 삭제 완료 시 통보(siteId, worklogId) — 페이지 본문 재조회용(SiteList §onDeleted 참조) */
  onSiteDeleted?: (siteId: number, worklogId: number) => void;
  /**
   * 헤더의 '+ 새 현장' 버튼 노출 여부(기본 true).
   * 현장을 새로 만들 일이 없는 화면(예: 고객리포트 설정 — 기존 현장 선택 전용)에서 false.
   */
  showRegister?: boolean;
}) {
  const { isOpen, toggle, initialStatus } = useSitePanel();
  const [regOpen, setRegOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  // 방금 등록한 현장 — 목록 맨 앞에 둔다(기획: 저장하면 목록 맨 위에 추가·자동 선택)
  const [pinSiteId, setPinSiteId] = useState<number | null>(null);

  // 현장 등록/확인 완료 → 그 현장을 현장패널에 선택(페이지가 나머지 해제) + 목록 재조회.
  // 공사현황(TS_WORKLOG) 생성은 팝업 Step D(공사기간)가 담당한다 — 여기서 만들면 중복 생성된다.
  function handleSiteRegistered(siteId: number) {
    setRegOpen(false);
    onSiteRegistered?.(siteId); // 페이지: 이 현장만 선택(나머지 해제)
    setPinSiteId(siteId);        // 목록 맨 앞에 끼워 넣기
    setReloadTick((t) => t + 1); // 현장 목록 재조회
  }

  return (
    <div
      className={`relative shrink-0 overflow-visible transition-[width] duration-200 ${
        isOpen ? 'w-[308px]' : 'w-0'
      }`}
    >
      {/* 원형 토글 버튼 — 항상 표시, 패널 왼쪽 끝에 고정 */}
      <button
        type="button"
        onClick={toggle}
        aria-label={isOpen ? '현장 패널 닫기' : '현장 패널 열기'}
        className="absolute -left-3.5 top-4 z-20 flex h-7 w-7 items-center justify-center rounded-full border border-surface-raised bg-group text-group-onPrimary shadow-md transition-transform duration-150 hover:scale-110 hover:bg-primary-600"
      >
        <svg
          width="15" height="15" viewBox="0 0 16 16" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          className={`transition-transform duration-200 ${isOpen ? '' : 'rotate-180'}`}
        >
          <polyline points="4 4 7 8 4 12" />
          <polyline points="9 4 12 8 9 12" />
        </svg>
      </button>

      {/* 패널 내용 — 열릴 때만 렌더 */}
      {isOpen && (
        <div className="flex h-full w-[308px] flex-col border-l border-border bg-surface-raised">
          {/* 패널 헤더 */}
          <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-3">
            <span className="text-sm font-semibold text-text">현장 목록</span>
            {showRegister && (
              <Button variant="primary" size="sm" onClick={() => setRegOpen(true)}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                새 현장
              </Button>
            )}
          </div>

          {/* 공사현황 현장 목록 */}
          <SiteList
            reloadTick={reloadTick}
            pinSiteId={pinSiteId}
            initialStatus={initialStatus}
            onFirstLoad={onFirstLoad}
            isSelected={isSiteSelected}
            onToggle={onToggleSite}
            onSitesLoaded={onSitesLoaded}
            onDeleted={onSiteDeleted}
          />
        </div>
      )}

      {/* 현장 등록 팝업 (공용 shared-features 재사용) */}
      {showRegister && regOpen && (
        <SiteRegistrationPopup
          agentId={WORK_LOG_AGENT_ID}
          onClose={() => setRegOpen(false)}
          onComplete={(siteId) => handleSiteRegistered(siteId)}
        />
      )}
    </div>
  );
}
